import { ChangeEvent, Key, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdOutlineAdd } from 'react-icons/md';
import { Button, SearchInput, Tab, Tabs } from '@ds';
import { AdminPageHeader, ConditionalRender, ConfirmationModal, DataTable, DeleteModal } from '@ui';
import {
  getRenderTXUsersInvitedTableCell,
  TX_USERS_INVITED_TABLE_COLUMNS,
  TXUsersInvitedFilters,
} from '@lp/features/admin/tables/TXUsersInvitedListTable';
import { getRenderTXUsersTableCell, getTXUsersTableColumns } from '@lp/features/admin/tables/TXUsersListTable';
import { TXUsersFilters } from '@lp/features/admin/tables/TXUsersListTable/components/TXUsersFilters';
import { AuthContext } from '@lp/core/auth';
import { useResendUserInviteMutation } from '@/queries/user';
import { useCustomerListQuery, useEnableCustomerOptimisticMutation } from '@/queries/customer';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { TxUserModalState } from '@/types/customer';
import { defaultTxUserListFilters } from '@/constants/customer.constants';
import { retrieveUnifiedError } from '@/api/utils';
import { CustomerListFilters, CustomerMT } from '@/api/apis/customer';
import { useWrappedRemoveCustomerMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';

const disabledFilterKeys: (keyof CustomerListFilters)[] = ['isVerified'];

export const TXUsersList = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<string>('users');
  const [customerToChange, setCustomerToChange] = useState<CustomerMT>();
  const [confirmationModalTitle, setConfirmationModalTitle] = useState<string>('');
  const [confirmationModalDesc, setConfirmationModalDesc] = useState<string>('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [activeModalState, setActiveModalState] = useState<TxUserModalState>({
    isOpen: false,
    title: '',
    description: '',
    customerId: '',
    currentActive: false,
  });
  const {
    filterQuery,
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: defaultTxUserListFilters(selectedTab === 'users'),
    query: useCustomerListQuery,
    disabledKeys: disabledFilterKeys,
  });

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.TxCenterCreate);

  const { mutateAsync: resendUserInvite } = useResendUserInviteMutation();
  const { mutateAsync: toggleCustomerEnabledAsync } = useEnableCustomerOptimisticMutation(filterQuery);

  const {
    isOpen,
    isLoading: isCustomerDeleting,
    onModalOpen,
    onModalClose,
    onEntityDelete,
  } = useEntityDelete({
    query: useWrappedRemoveCustomerMutation,
    successMessage: 'TX User deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/customers/tx_users/create');
  };

  const handleEdit = useCallback(
    (txUserId: string) => {
      navigate(`/admin/customers/tx_users/${txUserId}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (customerToChange) {
      setConfirmationModalOpen(true);
    }
  }, [customerToChange]);

  const handleResendInvite = async () => {
    try {
      await resendUserInvite(customerToChange!.userId!);

      toast.success('Invite sent successfully.');
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);

      toast.error('Something went wrong. Try again.');
    }
  };

  const onIsActiveSwitchChanged = useCallback((customerId: string, isActive: boolean) => {
    setActiveModalState({
      isOpen: true,
      title: `${isActive ? 'Activate' : 'Deactivate'} TX User?`,
      description: `Are you sure you want to ${isActive ? 'activate' : 'deactivate'} the TX User?`,
      customerId,
      currentActive: isActive,
    });
  }, []);

  const handleActiveConfirm = async () => {
    try {
      await toggleCustomerEnabledAsync({
        id: activeModalState.customerId,
        enabled: activeModalState.currentActive,
      });
      setActiveModalState((prev) => ({ ...prev, isOpen: false }));
    } catch (e) {
      const error = retrieveUnifiedError(e, 'Error while changing user active state');
      toast.error(error.message);
    }
  };

  const handleClearAll = useCallback(() => {
    clearFilters();
    updateFilters(selectedTab === 'users', 'isVerified');
    setSortBy(undefined);
    setSearchText('');
    setPage(1);
  }, [clearFilters, updateFilters, selectedTab, setSortBy, setSearchText, setPage]);

  const onResendInvite = useCallback((customer: CustomerMT) => {
    setConfirmationModalTitle('Resend Invite?');
    setConfirmationModalDesc(`Are you sure you want to resend invite to TX User ${customer?.email}?`);
    setCustomerToChange(customer);
  }, []);

  const renderTXUserInvitedTableCell = useMemo(() => {
    return getRenderTXUsersInvitedTableCell(onResendInvite, onModalOpen);
  }, [onResendInvite, onModalOpen]);

  const renderTXUserTableCell = useMemo(() => {
    return getRenderTXUsersTableCell(handleEdit, onModalOpen, onIsActiveSwitchChanged, hasCreatePermissions);
  }, [handleEdit, hasCreatePermissions, onIsActiveSwitchChanged, onModalOpen]);

  const onConfirmationModalClose = useCallback(() => {
    setCustomerToChange(undefined);
    setConfirmationModalOpen(false);
    setActiveModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const onTabChange = (key: Key) => {
    clearFilters();
    setSortBy(undefined);
    setSearchText('');
    updateFilters(key === 'users', 'isVerified');
    setSelectedTab(key as string);
  };

  const TX_USERS_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions
      ? getTXUsersTableColumns()
      : getTXUsersTableColumns().filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions]);

  const TX_USERS_INVITED_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions
      ? TX_USERS_INVITED_TABLE_COLUMNS
      : TX_USERS_INVITED_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="TX Users">
          <div className="w-[480px]">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <div className="flex w-full flex-col">
            <Tabs
              onSelectionChange={onTabChange}
              variant="underlined"
              classNames={{
                tabList: 'gap-8 w-full relative rounded-none p-0 border-none',
                cursor: 'w-full bg-[#EFF0F5]',
                tab: 'max-w-fit px-0 h-12',
                tabContent: 'group-data-[selected=true]:text-black h3',
              }}
              aria-label="Options"
              selectedKey={selectedTab}
            >
              <Tab key="users" title="Users">
                <div className="flex w-full flex-col gap-4">
                  <TXUsersFilters
                    filters={filters}
                    hasFilters={hasFilters}
                    updateFilters={updateFilters}
                    clearFilters={handleClearAll}
                  />
                  <DataTable
                    label="TX Users"
                    columns={TX_USERS_COLUMNS_FILTERED}
                    renderCell={renderTXUserTableCell}
                    data={data?.items || []}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    totalItems={data?.totalItems || 0}
                    totalPages={data?.totalPages || 1}
                    page={page}
                    itemsPerPage={itemsPerPage}
                    sortBy={sortBy}
                    onPageChange={setPage}
                    onItemsPerPageChange={setItemsPerPage}
                    onSortByChange={setSortBy}
                  />
                </div>
              </Tab>
              <Tab key="invitesSent" title="Invites sent">
                <div className="flex w-full flex-col gap-4">
                  <TXUsersInvitedFilters
                    filters={filters}
                    hasFilters={hasFilters}
                    updateFilters={updateFilters}
                    clearFilters={handleClearAll}
                  />
                  <DataTable
                    label="TX Users"
                    columns={TX_USERS_INVITED_TABLE_COLUMNS_FILTERED}
                    renderCell={renderTXUserInvitedTableCell}
                    data={data?.items || []}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    totalItems={data?.totalItems || 0}
                    totalPages={data?.totalPages || 1}
                    page={page}
                    itemsPerPage={itemsPerPage}
                    sortBy={sortBy}
                    onPageChange={setPage}
                    onItemsPerPageChange={setItemsPerPage}
                    onSortByChange={setSortBy}
                  />
                </div>
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
      <DeleteModal
        title="Delete TX User?"
        description="Are you sure you want to delete the TX User?"
        isOpen={isOpen}
        isLoading={isCustomerDeleting}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
      <ConfirmationModal
        title={confirmationModalTitle}
        description={confirmationModalDesc}
        isOpen={confirmationModalOpen}
        onChangeActive={handleResendInvite}
        onClose={onConfirmationModalClose}
      />
      <ConfirmationModal
        title={activeModalState.title}
        description={activeModalState.description}
        isOpen={activeModalState.isOpen}
        onChangeActive={handleActiveConfirm}
        onClose={onConfirmationModalClose}
      />
    </>
  );
};
